import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Leitura da plaqueta do exemplar. Usa a camera (BarcodeDetector, Chrome do
 * Android) e, onde nao houver suporte, aceita o codigo digitado. A plaqueta
 * traz `floraiq://tree/{codigo}`; tambem aceita o codigo CTA puro.
 */
type BarcodeDetectorLike = { detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]> };

function parseTreeCode(raw: string): string {
  const value = raw.trim();
  const m = value.match(/floraiq:\/\/tree\/(.+)$/i) ?? value.match(/\/tree\/([^/?#]+)/i);
  return decodeURIComponent((m ? m[1] : value).trim()).toUpperCase();
}

export default function FieldQrScanner() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraState, setCameraState] = useState<"starting" | "on" | "unsupported" | "denied">("starting");
  const [manual, setManual] = useState("");
  const [status, setStatus] = useState<{ kind: "idle" | "looking" | "error"; message?: string }>({ kind: "idle" });
  const busyRef = useRef(false);

  const resolve = async (raw: string) => {
    if (busyRef.current) return;
    const code = parseTreeCode(raw);
    if (!code) return;
    busyRef.current = true;
    setStatus({ kind: "looking", message: `Identificando ${code}...` });
    try {
      const tree = await api.treeByCode(code);
      navigate(`/coletor/exemplar/${tree.id}`);
    } catch {
      setStatus({ kind: "error", message: `Código ${code} não encontrado no CTA.` });
      busyRef.current = false;
    }
  };

  useEffect(() => {
    const Detector = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => BarcodeDetectorLike })
      .BarcodeDetector;
    if (!Detector || !navigator.mediaDevices?.getUserMedia) {
      setCameraState("unsupported");
      return;
    }
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;
    const detector = new Detector({ formats: ["qr_code"] });

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then(async (s) => {
        if (stopped) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = s;
        await video.play().catch(() => undefined);
        setCameraState("on");
        const tick = async () => {
          if (stopped) return;
          if (!busyRef.current && video.readyState >= 2) {
            try {
              const codes = await detector.detect(video);
              if (codes[0]?.rawValue) void resolve(codes[0].rawValue);
            } catch {
              /* quadro ilegivel: tenta o proximo */
            }
          }
          timer = window.setTimeout(tick, 350);
        };
        void tick();
      })
      .catch(() => setCameraState("denied"));

    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      {cameraState !== "unsupported" && cameraState !== "denied" && (
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-square">
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
          <div className="absolute inset-10 border-2 border-white/80 rounded-xl pointer-events-none" />
          {cameraState === "starting" && (
            <p className="absolute inset-x-0 bottom-3 text-center text-xs text-white/80">Abrindo a câmera...</p>
          )}
        </div>
      )}
      {cameraState === "unsupported" && (
        <p className="text-xs text-[#3F4A52]/70">
          Este navegador não lê QR Code pela câmera. Digite o código da plaqueta abaixo (no Android, use o Chrome).
        </p>
      )}
      {cameraState === "denied" && (
        <p className="text-xs text-[#D32F2F]">Sem acesso à câmera. Libere a câmera para este site ou digite o código.</p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void resolve(manual);
        }}
        className="flex gap-2"
      >
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          placeholder="Código CTA (ex.: CTA-1-000001)"
          autoCapitalize="characters"
          className="flex-1 min-w-0 px-3 py-3 rounded-xl border border-[#E8EAED] bg-white text-sm"
        />
        <button type="submit" className="px-4 rounded-xl bg-[#1B6E4F] text-white text-sm font-heading font-bold">
          Buscar
        </button>
      </form>

      {status.kind !== "idle" && (
        <p className={`text-sm ${status.kind === "error" ? "text-[#D32F2F]" : "text-[#3F4A52]"}`}>{status.message}</p>
      )}
    </div>
  );
}
